import { AddressLink } from 'Modules'
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router'

import { AppAdmin } from 'api/app-admin'
import { logError } from 'utils/logger'

interface PTOV3ContextType {
  code?: string | null
  loading?: boolean
  ptoDetail?: any
  contractAddress?: AddressLink | null
  getPTODetail: () => void
}

const PTOV3Context = createContext<PTOV3ContextType>({
  code: null,
  loading: false,
  ptoDetail: null,
  contractAddress: null,
  getPTODetail: () => {},
})

interface PTOV3ContextProps {
  children: ReactNode
}

/**
 * PTO v3 (ERC-3643) detail context - fetches GET /admin/pto/v3/:id.
 * `code` is the PTO _id from the route (/pto-v3/:code).
 */
const PTOV3Provider: React.FC<PTOV3ContextProps> = ({ children }) => {
  const locationParam = useParams()

  const code = locationParam.code || ''

  const [loading, setLoading] = useState(false)
  const [ptoDetail, setPTODetail] = useState<any>(null)
  const [contractAddress, setContractAddress] = useState<AddressLink | null>(null)

  const getPTODetail = useCallback(async () => {
    if (!code) return
    setLoading(true)
    try {
      const res = await AppAdmin.getPTOV3Detail(code)
      if (res.data.success) {
        const details = res.data.data
        setPTODetail(details)
        setContractAddress(details?.contractAddress || null)
      }
    } catch (error) {
      setPTODetail(null)
      setContractAddress(null)
      logError('Error fetching PTO v3 details: ', error)
    } finally {
      setLoading(false)
    }
  }, [code])

  useEffect(() => {
    getPTODetail()
  }, [getPTODetail])

  return (
    <PTOV3Context.Provider value={{ code, loading, ptoDetail, contractAddress, getPTODetail }}>
      {children}
    </PTOV3Context.Provider>
  )
}

const usePTOV3Context = (): PTOV3ContextType => {
  const context = useContext(PTOV3Context)
  if (context === undefined) {
    throw new Error('PTOV3Context not available')
  }
  return context
}

export { PTOV3Provider, usePTOV3Context }
